<style scoped>

.dashboard_vifs {
    padding: 24px;
}

.dashboard_vifs_controls {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.dashboard_vifs_select {
    flex: 0 1 280px;
    min-width: 220px;
}

.dashboard_vifs_ranges {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.dashboard_vifs_chart_container {
    min-height: 440px;
    position: relative;
}

.dashboard_vifs_chart {
    height: 440px;
    width: 100%;
}

.dashboard_vifs_no_data {
    color: rgba(0, 0, 0, 0.6);
    left: 0;
    position: absolute;
    right: 0;
    text-align: center;
    top: 180px;
}

.br {
    color: rgba(109, 103, 164);
}

</style>

<template>
<div class="dashboard_vifs">
    <h1 class="br mb-4">VIF traffic</h1>

    <div class="dashboard_vifs_controls">
        <v-select
            v-model="selected_vif"
            class="dashboard_vifs_select"
            :items="vif_options"
            item-text="label"
            item-value="value"
            label="VIF"
            dense
            outlined
            hide-details
            :disabled="vifs_loading || vif_options.length === 0"
            :loading="vifs_loading"
            @change="change_vif"
        ></v-select>
        <v-select
            v-model="selected_resolution"
            class="dashboard_vifs_select"
            :items="data_intervals"
            item-text="label"
            item-value="value"
            label="Data interval"
            dense
            outlined
            hide-details
            @change="change_resolution"
        ></v-select>
        <div class="dashboard_vifs_ranges">
            <v-btn-toggle
                v-model="selected_range_ms"
                mandatory
                dense
                @change="change_range"
            >
                <v-btn
                    v-for="range in available_ranges"
                    :key="range.label"
                    :value="range.value"
                    small
                >
                    {{ range.label }}
                </v-btn>
            </v-btn-toggle>
            <v-btn
                class="ml-2"
                small
                :disabled="loading || selected_vif === null"
                @click="load_traffic"
            >
                Refresh
            </v-btn>
        </div>
    </div>

    <v-card outlined>
        <v-card-title>
            VIF {{ selected_vif === null ? "-" : selected_vif }} traffic
        </v-card-title>
        <v-card-subtitle v-if="host && selected_vif !== null">
            Host {{ host }}, BR instance {{ instance }}, VIF {{ selected_vif }}
        </v-card-subtitle>
        <v-progress-linear
            v-if="loading || vifs_loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-alert
            v-if="error_message"
            class="ma-4"
            type="error"
            outlined
        >
            {{ error_message }}
        </v-alert>
        <v-card-text>
            <div class="dashboard_vifs_chart_container">
                <div ref="traffic_chart" class="dashboard_vifs_chart"></div>
                <div
                    v-if="!loading && !vifs_loading &&
                        !error_message && points_count === 0"
                    class="dashboard_vifs_no_data"
                >
                    <div>{{ no_data_message }}</div>
                    <v-btn
                        v-if="vif_options.length === 0"
                        class="mt-3"
                        color="primary"
                        small
                        text
                        to="/dashboard/vifs/monitoring"
                    >
                        Select VIFs
                    </v-btn>
                </div>
            </div>
        </v-card-text>
    </v-card>

    <v-card class="mt-4" outlined>
        <v-card-title>
            VIF {{ selected_vif === null ? "-" : selected_vif }} packets
        </v-card-title>
        <v-progress-linear
            v-if="loading || vifs_loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-card-text>
            <div class="dashboard_vifs_chart_container">
                <div ref="packet_chart" class="dashboard_vifs_chart"></div>
                <div
                    v-if="!loading && !vifs_loading &&
                        !error_message && points_count === 0"
                    class="dashboard_vifs_no_data"
                >
                    {{ no_data_message }}
                </div>
            </div>
        </v-card-text>
    </v-card>
</div>
</template>

<script>

import * as echarts from "echarts";
import api_client from "../services/api.js";


const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const TRAFFIC_SERIES = [
    { name: "RX", field: "rx_bps", area: true },
    { name: "TX", field: "tx_bps", area: true },
];

const PACKET_SERIES = [
    { name: "RX packets", field: "rx_pps" },
    { name: "TX packets", field: "tx_pps" },
];

const DATA_INTERVALS = [
    {
        label: "1 minute — last 2 weeks",
        value: "1m",
        default_range_ms: DAY_MS,
        ranges: [
            { label: "1 hour", value: HOUR_MS },
            { label: "24 hours", value: DAY_MS },
            { label: "7 days", value: 7 * DAY_MS },
            { label: "14 days", value: 14 * DAY_MS },
        ],
    },
    {
        label: "5 minutes — last 30 days",
        value: "5m",
        default_range_ms: 30 * DAY_MS,
        ranges: [
            { label: "24 hours", value: DAY_MS },
            { label: "7 days", value: 7 * DAY_MS },
            { label: "14 days", value: 14 * DAY_MS },
            { label: "30 days", value: 30 * DAY_MS },
        ],
    },
    {
        label: "1 hour — last year",
        value: "1h",
        default_range_ms: 365 * DAY_MS,
        ranges: [
            { label: "7 days", value: 7 * DAY_MS },
            { label: "30 days", value: 30 * DAY_MS },
            { label: "90 days", value: 90 * DAY_MS },
            { label: "1 year", value: 365 * DAY_MS },
        ],
    },
];


function format_number(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 100) {
        return value.toFixed(0);
    }
    if (absolute_value >= 10) {
        return value.toFixed(1);
    }
    return value.toFixed(2);
}


function rate_unit(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 1e9) {
        return { divisor: 1e9, prefix: "G" };
    }
    if (absolute_value >= 1e6) {
        return { divisor: 1e6, prefix: "M" };
    }
    if (absolute_value >= 1e3) {
        return { divisor: 1e3, prefix: "K" };
    }
    return { divisor: 1, prefix: "" };
}


function format_axis_rate(value) {
    const unit = rate_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}`.trim();
}


function format_tooltip_rate(value, suffix) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
    }
    const unit = rate_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}${suffix}`;
}


function chart_option(
    points,
    definitions,
    colors,
    axis_name,
    tooltip_suffix,
    zoom_name,
    zoom_start,
    zoom_end
) {
    return {
        animation: false,
        color: colors,
        tooltip: {
            trigger: "axis",
            formatter: parameters => {
                if (!parameters.length) {
                    return "";
                }
                const timestamp = new Date(
                    parameters[0].value[0]
                ).toLocaleString();
                const values = parameters.map(parameter => (
                    `${parameter.marker}${parameter.seriesName}: ` +
                    format_tooltip_rate(
                        parameter.value[1],
                        tooltip_suffix
                    )
                ));
                return [timestamp].concat(values).join("<br>");
            },
        },
        legend: {
            bottom: 45,
            data: definitions.map(series => series.name),
        },
        grid: {
            bottom: 86,
            containLabel: true,
            left: 20,
            right: 24,
            top: 48,
        },
        xAxis: {
            type: "time",
            boundaryGap: false,
            axisLabel: {
                hideOverlap: true,
            },
        },
        yAxis: {
            type: "value",
            min: 0,
            name: axis_name,
            axisLabel: {
                formatter: format_axis_rate,
            },
        },
        dataZoom: [
            {
                id: `${zoom_name}_inside_zoom`,
                type: "inside",
                filterMode: "none",
                start: zoom_start,
                end: zoom_end,
                xAxisIndex: 0,
            },
            {
                id: `${zoom_name}_slider_zoom`,
                type: "slider",
                filterMode: "none",
                start: zoom_start,
                end: zoom_end,
                xAxisIndex: 0,
                bottom: 8,
                height: 28,
                realtime: true,
                showDataShadow: true,
                showDetail: true,
                zoomLock: false,
                left: 130,
                right: 130,
            },
        ],
        series: definitions.map(definition => ({
            name: definition.name,
            type: "line",
            data: points.map(point => [
                point.time,
                point[definition.field],
            ]),
            showSymbol: false,
            sampling: "lttb",
            ...(definition.area ? { areaStyle: { opacity: 0.12 } } : {}),
        })),
    };
}


function request_error_message(error) {
    const response = error.response;
    return response && response.data
        ? response.data.detail || "Dashboard request failed"
        : "Dashboard request failed";
}


export default {
    name: "dashboard_vifs",

    data: () => ({
        data_intervals: DATA_INTERVALS,
        error_message: "",
        host: "",
        instance: 1,
        loading: false,
        points_count: 0,
        request_id: 0,
        selected_range_ms: DAY_MS,
        selected_resolution: "1m",
        selected_vif: null,
        vif_options: [],
        vifs_loading: false,
        zoom_end: 100,
        zoom_start: 0,
    }),

    computed: {
        available_ranges() {
            return this.selected_interval.ranges;
        },

        no_data_message() {
            if (this.vif_options.length === 0) {
                return "No VIFs are selected for monitoring.";
            }
            return "No traffic data is available for this time range.";
        },

        selected_interval() {
            return this.data_intervals.find(interval => (
                interval.value === this.selected_resolution
            )) || this.data_intervals[0];
        },
    },

    mounted() {
        this.traffic_chart = echarts.init(this.$refs.traffic_chart);
        this.packet_chart = echarts.init(this.$refs.packet_chart);
        this.chart_group = `dashboard_vif_${this._uid}`;
        this.traffic_chart.group = this.chart_group;
        this.packet_chart.group = this.chart_group;
        echarts.connect(this.chart_group);
        this.traffic_chart.on("datazoom", this.remember_zoom);
        this.packet_chart.on("datazoom", this.remember_zoom);
        window.addEventListener("resize", this.resize_charts);
        this.load_vifs().then(() => {
            if (this.selected_vif !== null) {
                this.load_traffic();
            }
        });
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.resize_charts);
        if (this.chart_group) {
            echarts.disconnect(this.chart_group);
        }
        if (this.traffic_chart) {
            this.traffic_chart.off("datazoom", this.remember_zoom);
            this.traffic_chart.dispose();
            this.traffic_chart = null;
        }
        if (this.packet_chart) {
            this.packet_chart.off("datazoom", this.remember_zoom);
            this.packet_chart.dispose();
            this.packet_chart = null;
        }
    },

    methods: {
        change_vif() {
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_traffic();
        },

        change_range() {
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_traffic();
        },

        change_resolution() {
            this.selected_range_ms = this.selected_interval.default_range_ms;
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_traffic();
        },

        remember_zoom(event) {
            const data_zoom = event && event.batch
                ? event.batch[0]
                : event;

            if (data_zoom && Number.isFinite(data_zoom.start)) {
                this.zoom_start = data_zoom.start;
            }
            if (data_zoom && Number.isFinite(data_zoom.end)) {
                this.zoom_end = data_zoom.end;
            }
        },

        resize_charts() {
            if (this.traffic_chart) {
                this.traffic_chart.resize();
            }
            if (this.packet_chart) {
                this.packet_chart.resize();
            }
        },

        load_vifs() {
            this.vifs_loading = true;
            this.error_message = "";
            return api_client.run_get_query('/dashboard/vif_monitoring')
            .then(response => {
                const data = response.data || {};
                const vifs = Array.isArray(data.vifs) ? data.vifs : [];
                const names = vifs
                    .filter(vif => vif && typeof vif.name === "string")
                    .map(vif => vif.name);

                this.instance = data.instance || 1;
                this.vif_options = names.map(name => ({
                    label: name,
                    value: name,
                }));

                if (!names.includes(this.selected_vif)) {
                    this.selected_vif = names[0] || null;
                }
            })
            .catch(error => {
                this.error_message = request_error_message(error);
                this.vif_options = [];
                this.selected_vif = null;
                this.points_count = 0;
                this.render_charts([]);
            })
            .finally(() => {
                this.vifs_loading = false;
            });
        },

        load_traffic() {
            if (this.selected_vif === null) {
                return Promise.resolve();
            }

            const end_time = new Date();
            const start_time = new Date(
                end_time.getTime() - this.selected_range_ms
            );
            const request_id = ++this.request_id;

            this.loading = true;
            this.error_message = "";
            return api_client.run_get_query('/dashboard/vif_traffic', {
                start: start_time.toISOString(),
                end: end_time.toISOString(),
                vif: this.selected_vif,
                resolution: this.selected_resolution,
            })
            .then(response => {
                if (request_id !== this.request_id) {
                    return;
                }
                const data = response.data || {};
                const points = Array.isArray(data.points) ? data.points : [];
                this.host = data.host || "";
                this.instance = data.instance || 1;
                this.points_count = points.length;
                this.render_charts(points);
            })
            .catch(error => {
                if (request_id !== this.request_id) {
                    return;
                }
                this.error_message = request_error_message(error);
                this.points_count = 0;
                this.render_charts([]);
            })
            .finally(() => {
                if (request_id === this.request_id) {
                    this.loading = false;
                }
            });
        },

        render_charts(points) {
            this.traffic_chart.setOption(chart_option(
                points,
                TRAFFIC_SERIES,
                ["#2878b5", "#2a9d55"],
                "bit/s",
                "bit/s",
                "traffic",
                this.zoom_start,
                this.zoom_end
            ), true);

            this.packet_chart.setOption(chart_option(
                points,
                PACKET_SERIES,
                ["#2878b5", "#2a9d55"],
                "pkt/s",
                "pkt/s",
                "packet",
                this.zoom_start,
                this.zoom_end
            ), true);
        },
    },
};

</script>
